import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { HelpButton } from '../components/ui/help-button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Users, Mail, MousePointer, Clock, Plus, Play, Eye, Target, AlertTriangle,
  CheckCircle, Loader2, TrendingUp, X, Info, ClipboardList, ArrowRight, ArrowLeft, Award, RotateCcw
} from 'lucide-react';
import { getUserErrorMessage } from '../utils/errorMessages';
import { toast } from 'sonner';
import WizardStepper from '../components/WizardStepper';
import WizardNavButtons from '../components/WizardNavButtons';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { Coachmark } from '../components/Coachmark';
import { SteekproefBanner } from '../components/SteekproefBanner';

const PeoplePage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Human Risk Assessment states
  const [hrView, setHrView] = useState('list'); // 'list' or 'questionnaire'
  const [hrAssessmentId, setHrAssessmentId] = useState(null);
  const [hrQuestion, setHrQuestion] = useState(null);
  const [hrAnswer, setHrAnswer] = useState('');
  const [hrNotes, setHrNotes] = useState('');
  const [hrSubmitting, setHrSubmitting] = useState(false);
  const [hrProgress, setHrProgress] = useState({ answered: 0, total: 15 });
  const [creating, setCreating] = useState(false);
  const [usageLimits, setUsageLimits] = useState({ campaigns: { used: 0, limit: 1 } });
  const [moduleStats, setModuleStats] = useState(null);
  const [viewHRAnswers, setViewHRAnswers] = useState(false);
  const [hrAnswersData, setHrAnswersData] = useState([]);
  const [steekproefCtx, setSteekproefCtx] = useState(null);

  const [campaignName, setCampaignName] = useState('');
  const [targetEmails, setTargetEmails] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, campaignsRes, limitsRes, peopleS, processS, techS, spCtx] = await Promise.all([
        api.get('/people/stats'),
        api.get('/people/campaigns'),
        api.get('/ggrc/usage-limits').catch(() => ({ data: { campaigns: { used: 0, limit: 1 } } })),
        api.get('/people/stats').catch(() => ({ data: { completed: 0, total: 10, score: 0 } })),
        api.get('/process/stats').catch(() => ({ data: { completed: 0, total: 10, score: 0 } })),
        api.get('/technology/stats').catch(() => ({ data: { completed: 0, total: 10, score: 0 } })),
        api.get('/assessments/active-reassessment-context').catch(() => ({ data: { active: false } })),
      ]);
      setStats(statsRes.data);
      setCampaigns(campaignsRes.data);
      setUsageLimits(limitsRes.data || { campaigns: { used: 0, limit: 1 } });
      setModuleStats({
        people: peopleS.data || { completed: 0, total: 10, score: 0 },
        process: processS.data || { completed: 0, total: 10, score: 0 },
        technology: techS.data || { completed: 0, total: 10, score: 0 },
      });
      setSteekproefCtx(spCtx.data);
    } catch (error) {
      toast.error('De gegevens konden niet worden geladen. Ververs de pagina.');
    } finally { setLoading(false); }
  };

  const canCreateCampaign = usageLimits.campaigns?.used < usageLimits.campaigns?.limit;

  const handleCreateCampaign = async () => {
    if (!campaignName || !targetEmails.trim()) {
      toast.error('Vul alle velden in');
      return;
    }
    const emails = targetEmails.split('\n').map(e => e.trim()).filter(e => e && e.includes('@'));
    if (emails.length === 0) {
      toast.error('Voer minimaal een geldig e-mailadres in');
      return;
    }
    setCreating(true);
    try {
      // Use default template since template field is removed
      const templates = (await api.get('/people/templates')).data;
      const defaultTemplate = templates[0]?.id || 'template_1';
      await api.post('/people/campaigns', {
        name: campaignName,
        template_id: defaultTemplate,
        target_emails: emails
      });
      toast.success('Campagne aangemaakt');
      setShowCreateModal(false);
      setCampaignName('');
      setTargetEmails('');
      fetchData();
    } catch (error) {
      const detail = getUserErrorMessage(error, 'De campagne kon niet worden aangemaakt. Controleer uw invoer.');
      toast.error(detail);
    } finally { setCreating(false); }
  };

  const handleStartCampaign = async (campaignId) => {
    try {
      await api.post(`/people/campaigns/${campaignId}/start`);
      toast.success('Campagne gestart');
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De campagne kon niet worden gestart. Probeer het later opnieuw.'));
    }
  };

  const handleViewCampaign = async (campaignId) => {
    try {
      const response = await api.get(`/people/campaigns/${campaignId}`);
      setSelectedCampaign(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error('De details konden niet worden geladen. Probeer het opnieuw.');
    }
  };

  const handleStartHRAssessment = async () => {
    try {
      const res = await api.post('/people/assessment/start');
      const id = res.data.id;
      setHrAssessmentId(id);
      const detailRes = await api.get(`/people/assessment/${id}`);
      setHrQuestion(detailRes.data.current_question);
      setHrProgress({ answered: detailRes.data.answered_count, total: detailRes.data.total_questions });
      setHrView('questionnaire');
      setHrAnswer('');
      setHrNotes('');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Het assessment kon niet worden gestart. Probeer het later opnieuw.'));
    }
  };

  const handleViewHRAnswers = async () => {
    try {
      const res = await api.get('/people/assessment/answers');
      setHrAnswersData(res.data.answers || []);
      setViewHRAnswers(true);
    } catch (error) {
      toast.error('Antwoorden konden niet worden geladen.');
    }
  };

  const handleHRAnswer = async () => {
    if (!hrAnswer) { toast.error('Selecteer een antwoord'); return; }
    setHrSubmitting(true);
    try {
      const res = await api.post(`/people/assessment/${hrAssessmentId}/answer`, { question_id: hrQuestion?.id || '', answer: hrAnswer, notes: hrNotes });
      if (res.data.is_completed) {
        toast.success(`Assessment voltooid! Score: ${res.data.score}%`);
        setHrView('list');
        fetchData();
      } else {
        setHrQuestion(res.data.next_question);
        setHrProgress(prev => ({ ...prev, answered: prev.answered + 1 }));
        setHrAnswer('');
        setHrNotes('');
      }
    } catch (error) {
      toast.error('Uw antwoord kon niet worden opgeslagen. Probeer het opnieuw.');
    } finally { setHrSubmitting(false); }
  };

  const handleHRSaveLater = () => {
    toast.success('Voortgang opgeslagen. U kunt later verdergaan.');
    setHrView('list');
    fetchData();
  };

  const getStatusBadge = (status) => {
    const map = {
      draft: { cls: 'border-neutral-200 bg-neutral-50 text-neutral-600', label: 'Concept' },
      scheduled: { cls: 'border-blue-200 bg-blue-50 text-blue-700', label: 'Gepland' },
      running: { cls: 'border-orange-200 bg-orange-50 text-orange-700', label: 'Actief' },
      completed: { cls: 'border-green-200 bg-green-50 text-green-700', label: 'Afgerond' },
      cancelled: { cls: 'border-red-200 bg-red-50 text-red-700', label: 'Geannuleerd' },
    };
    const m = map[status] || map.draft;
    return <Badge className={m.cls}>{m.label}</Badge>;
  };

  if (loading) {
    return (<MainLayout><div className="flex items-center justify-center h-[calc(100vh-4rem)]"><Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} /></div></MainLayout>);
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-5xl px-6 py-8 space-y-10">
        <WizardStepper moduleStats={moduleStats} />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">People Module</p>
            <h1 className="text-2xl font-bold tracking-tight text-black">Phishing Simulaties</h1>
            <p className="text-sm text-neutral-600 mt-1">Test de weerbaarheid van uw medewerkers tegen phishing-aanvallen</p>
          </div>
          <div className="flex items-center gap-3">
            <HelpButton steps={[
              { title: 'Phishing Simulaties', description: 'Test uw medewerkers met nep-phishingmails en meet wie er op verdachte links klikt.', target: '[data-testid="create-campaign-button"]' },
              { title: 'HR Vragenlijst', description: 'Stel bewustzijnsvragen aan medewerkers om het kennisniveau rondom cybersecurity te meten.', target: '[data-testid="hr-questionnaire"]', tip: 'De klikratio geeft een indicatie van het risico op social engineering in uw organisatie.' },
              { title: 'Campagne Resultaten', description: 'Bekijk per campagne hoeveel e-mails verzonden zijn en hoeveel medewerkers geklikt hebben.', target: null },
            ]} />
            <Button
              className="bg-black text-white hover:bg-neutral-800"
              onClick={() => setShowCreateModal(true)}
              data-testid="create-campaign-button"
            >
              <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />Nieuwe Campagne
            </Button>
          </div>
        </div>

        {/* Coachmark */}
        <Coachmark
          id="people-intro"
          text="Begin met de HR vragenlijst om het bewustzijnsniveau van je medewerkers te meten. Daarna kun je een phishing-simulatie starten."
        />

        {/* Steekproef Banner */}
        {steekproefCtx?.active && (
          <SteekproefBanner
            active={true}
            moduleName="people"
            moduleLabel="Mensen"
            reassessment={steekproefCtx.reassessment}
            nulmetingSnapshot={steekproefCtx.nulmeting_snapshot}
            isModuleIncluded={(steekproefCtx.reassessment?.modules || []).includes('people') || (steekproefCtx.reassessment?.modules || []).includes('all')}
          />
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Mail, val: stats?.total_sent || 0, lbl: 'Emails verzonden', bg: 'blue' },
            { icon: Eye, val: `${stats?.open_rate || 0}%`, lbl: 'Open rate', bg: 'orange' },
            { icon: MousePointer, val: `${stats?.click_rate || 0}%`, lbl: 'Click rate', bg: 'red' },
            { icon: Target, val: stats?.vulnerability_score || 100, lbl: 'Security Score', bg: 'green' },
          ].map((s, i) => (
            <Card key={i} className="border-neutral-200 shadow-sm">
              <CardContent className="p-5"><div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg bg-${s.bg}-50${s.bg}-900/30 flex items-center justify-center`}>
                  <s.icon className={`h-5 w-5 text-${s.bg}-600`} strokeWidth={1.5} />
                </div>
                <div><p className="text-2xl font-bold text-black">{s.val}</p><p className="text-xs text-neutral-500">{s.lbl}</p></div>
              </div></CardContent>
            </Card>
          ))}
        </div>

        {/* Human Risk Assessment Section */}
        {hrView === 'questionnaire' ? (
          <Card className="border-neutral-200 shadow-sm" data-testid="hr-questionnaire">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold text-black">Human Risk Assessment</CardTitle>
                <Button variant="ghost" size="sm" className="text-xs text-neutral-500" onClick={handleHRSaveLater}><ArrowLeft className="h-3 w-3 mr-1" strokeWidth={1.5} />Opslaan & later</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs text-neutral-500">
                  <span>Vraag {hrProgress.answered + 1} van {hrProgress.total}</span>
                  <span>{Math.round((hrProgress.answered / hrProgress.total) * 100)}%</span>
                </div>
                <Progress value={(hrProgress.answered / hrProgress.total) * 100} className="h-2" />
              </div>

              {hrQuestion && (
                <div className="space-y-6">
                  <div>
                    <Badge variant="outline" className="border-neutral-200 text-[10px] mb-3">{hrQuestion.category}</Badge>
                    <h3 className="text-sm font-semibold text-black leading-relaxed">{hrQuestion.question}</h3>
                  </div>

                  <RadioGroup value={hrAnswer} onValueChange={setHrAnswer} className="space-y-2" data-testid="hr-answer-options">
                    {[
                      { value: 'yes', label: 'Ja', desc: 'Volledig geïmplementeerd en consistent toegepast' },
                      { value: 'partial', label: 'Gedeeltelijk', desc: 'Deels aanwezig maar niet consistent' },
                      { value: 'no', label: 'Nee', desc: 'Niet geïmplementeerd of niet aanwezig' },
                      { value: 'unknown', label: 'Onbekend', desc: 'Kan ik op dit moment niet beoordelen' },
                    ].map(opt => (
                      <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${hrAnswer === opt.value ? 'border-black bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'}`}>
                        <RadioGroupItem value={opt.value} />
                        <div>
                          <p className="text-sm font-medium text-black">{opt.label}</p>
                          <p className="text-xs text-neutral-500">{opt.desc}</p>
                        </div>
                      </label>
                    ))}
                  </RadioGroup>

                  <div>
                    <Label className="text-xs text-neutral-500">Toelichting (optioneel)</Label>
                    <Textarea value={hrNotes} onChange={e => setHrNotes(e.target.value)} placeholder="Eventuele toelichting bij uw antwoord..." className="mt-1 text-sm" rows={2} />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button onClick={handleHRAnswer} disabled={hrSubmitting || !hrAnswer} className="bg-black text-white hover:bg-neutral-800" data-testid="hr-next-button">
                      {hrSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : hrProgress.answered + 1 === hrProgress.total ? <><Award className="h-4 w-4 mr-1" strokeWidth={1.5} />Afronden</> : <>Volgende<ArrowRight className="ml-1 h-4 w-4" strokeWidth={1.5} /></>}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-neutral-200 shadow-sm" data-testid="hr-assessment-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">Human Risk Assessment</CardTitle>
                {stats?.hr_assessment_status === 'completed' ? (
                  <Badge className="bg-green-100 text-green-700">Score: {stats.hr_assessment_score}%</Badge>
                ) : stats?.hr_assessment_status === 'in_progress' ? (
                  <Badge className="bg-amber-100 text-amber-700">Bezig: {stats.hr_assessment_answered}/{stats.hr_assessment_total}</Badge>
                ) : null}
              </div>
            </CardHeader>
            <CardContent>
              {stats?.hr_assessment_status === 'completed' ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                      <Award className="h-6 w-6 text-green-600" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-green-800">Assessment voltooid</p>
                      <p className="text-xs text-green-600">Uw human risk score is {stats.hr_assessment_score}%. Dit weerspiegelt het beveiligingsbewustzijn en -gedrag van uw organisatie.</p>
                    </div>
                    <Button size="sm" variant="outline" className="border-green-300 text-green-700" onClick={handleStartHRAssessment} data-testid="hr-revise-button">
                      <RotateCcw className="h-3 w-3 mr-1" strokeWidth={1.5} />Herzien
                    </Button>
                    <Button size="sm" variant="outline" className="border-neutral-200 text-neutral-600" onClick={() => handleViewHRAnswers()} data-testid="hr-view-answers-button">
                      <Eye className="h-3 w-3 mr-1" strokeWidth={1.5} />Inzien
                    </Button>
                  </div>
                </div>
              ) : stats?.hr_assessment_status === 'in_progress' ? (
                <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <ClipboardList className="h-6 w-6 text-amber-600" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800">Assessment niet voltooid</p>
                    <p className="text-xs text-amber-600">{stats.hr_assessment_answered} van {stats.hr_assessment_total} vragen beantwoord</p>
                    <Progress value={(stats.hr_assessment_answered / stats.hr_assessment_total) * 100} className="h-1.5 mt-2" />
                  </div>
                  <Button size="sm" className="bg-black text-white hover:bg-neutral-800" onClick={handleStartHRAssessment} data-testid="hr-resume-button">
                    Hervat<ArrowRight className="ml-1 h-3 w-3" strokeWidth={1.5} />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <ClipboardList className="h-6 w-6 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-800">Meet het beveiligingsbewustzijn</p>
                    <p className="text-xs text-blue-600">Beantwoord 15 vragen over awareness, wachtwoordbeleid, incidentmelding en meer. Geschatte tijd: 5 minuten.</p>
                  </div>
                  <Button size="sm" className="bg-black text-white hover:bg-neutral-800" onClick={handleStartHRAssessment} data-testid="hr-start-button">
                    <Play className="h-3 w-3 mr-1" strokeWidth={1.5} />Start Assessment
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Campaigns List */}
        <Card className="border-neutral-200 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">Campagnes</CardTitle></CardHeader>
          <CardContent>
            {campaigns.length > 0 ? (
              <div className="divide-y divide-neutral-100">
                {campaigns.map(campaign => (
                  <div key={campaign.id} className="flex items-center gap-4 py-4" data-testid={`campaign-row-${campaign.id}`}>
                    <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-black">{campaign.name}</p>
                      <p className="text-xs text-neutral-500">{campaign.total_recipients} ontvangers</p>
                    </div>
                    <div className="text-right">
                      {getStatusBadge(campaign.status)}
                      {(campaign.status === 'running' || campaign.status === 'completed') && (
                        <p className="text-xs text-neutral-500 mt-1">{campaign.clicked_count}/{campaign.sent_count} geklikt</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {campaign.status === 'draft' && (
                        <Button size="sm" className="bg-black text-white hover:bg-neutral-800" onClick={() => handleStartCampaign(campaign.id)} data-testid={`start-campaign-${campaign.id}`}>
                          <Play className="h-3 w-3 mr-1" strokeWidth={1.5} />Start
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="border-neutral-200" onClick={() => handleViewCampaign(campaign.id)} data-testid={`view-campaign-${campaign.id}`}>
                        <Eye className="h-3 w-3 mr-1" strokeWidth={1.5} />Details
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <Mail className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-lg font-medium text-black mb-2">Geen campagnes</h3>
                <p className="text-sm text-neutral-500 mb-4">Start uw eerste phishing simulatie om de weerbaarheid van uw medewerkers te testen.</p>
                  <Button className="bg-black text-white hover:bg-neutral-800" onClick={() => setShowCreateModal(true)}>
                    <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />Eerste Campagne
                  </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Volgende stap */}
        <section className="rounded-2xl border border-neutral-200 p-6" data-testid="people-next-step">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="spk-section-label mb-0">Volgende stap</p>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black text-white">Aanbevolen</span>
              </div>
              <h3 className="text-lg font-semibold mt-1">Ga verder met Organisatie</h3>
              <p className="text-sm text-neutral-600 mt-1">Evalueer je processen en beleidsrichtlijnen met compliance-vragenlijsten.</p>
            </div>
            <Button
              onClick={() => window.location.href = '/process'}
              className="bg-black text-white hover:bg-neutral-800 shrink-0"
              data-testid="people-goto-process"
            >
              Organisatie
              <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
            </Button>
          </div>
        </section>

        <WizardNavButtons />
      </div>
      </div>

      {/* Create Campaign Modal - Template field REMOVED */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nieuwe Phishing Campagne</DialogTitle>
            <DialogDescription>Maak een nieuwe phishing simulatie. Een standaard template wordt automatisch toegepast.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <Label htmlFor="campaignName">Campagne Naam</Label>
              <Input id="campaignName" value={campaignName} onChange={e => setCampaignName(e.target.value)} placeholder="Q1 2026 Phishing Test" data-testid="campaign-name-input" />
            </div>
            <div className="space-y-3">
              <Label htmlFor="targetEmails">Doelgroep (een e-mail per regel)</Label>
              <textarea id="targetEmails" value={targetEmails} onChange={e => setTargetEmails(e.target.value)}
                placeholder={"medewerker1@bedrijf.nl\nmedewerker2@bedrijf.nl\nmedewerker3@bedrijf.nl"}
                rows={5} className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-black resize-none" data-testid="target-emails-input" />
              <p className="text-xs text-neutral-500">{targetEmails.split('\n').filter(e => e.trim() && e.includes('@')).length} geldige e-mailadressen</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuleren</Button>
            <Button className="bg-black text-white hover:bg-neutral-800" onClick={handleCreateCampaign} disabled={creating} data-testid="create-campaign-submit">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Campagne Aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedCampaign?.name}</DialogTitle>
            <DialogDescription>Campagne details en resultaten</DialogDescription>
          </DialogHeader>
          {selectedCampaign && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-4 gap-6">
                {[
                  { val: selectedCampaign.sent_count, lbl: 'Verzonden', color: 'text-black' },
                  { val: selectedCampaign.opened_count, lbl: 'Geopend', color: 'text-black' },
                  { val: selectedCampaign.clicked_count, lbl: 'Geklikt', color: 'text-red-600' },
                  { val: selectedCampaign.sent_count > 0 ? Math.round((selectedCampaign.clicked_count / selectedCampaign.sent_count) * 100) + '%' : '0%', lbl: 'Click Rate', color: 'text-black' },
                ].map((s, i) => (
                  <div key={i} className="text-center p-3 bg-neutral-50 rounded-lg">
                    <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
                    <p className="text-xs text-neutral-500">{s.lbl}</p>
                  </div>
                ))}
              </div>
              <div>
                <h4 className="text-sm font-medium text-black mb-3">Ontvangers</h4>
                <div className="max-h-64 overflow-y-auto border border-neutral-200 rounded-lg">
                  <table className="w-full">
                    <thead className="bg-neutral-50 sticky top-0">
                      <tr>
                        {['Email', 'Verzonden', 'Geopend', 'Geklikt'].map((h, i) => (
                          <th key={i} className={`text-${i === 0 ? 'left' : 'center'} text-xs font-medium text-neutral-500 p-3`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {selectedCampaign.recipients?.map((r, i) => (
                        <tr key={i}>
                          <td className="p-3 text-sm text-neutral-600">{r.email}</td>
                          <td className="p-3 text-center">{r.sent ? <CheckCircle className="h-4 w-4 text-green-500 mx-auto" strokeWidth={1.5} /> : <Clock className="h-4 w-4 text-neutral-300 mx-auto" strokeWidth={1.5} />}</td>
                          <td className="p-3 text-center">{r.opened ? <CheckCircle className="h-4 w-4 text-orange-500 mx-auto" strokeWidth={1.5} /> : <X className="h-4 w-4 text-neutral-300 mx-auto" strokeWidth={1.5} />}</td>
                          <td className="p-3 text-center">{r.clicked ? <AlertTriangle className="h-4 w-4 text-red-500 mx-auto" strokeWidth={1.5} /> : <CheckCircle className="h-4 w-4 text-green-500 mx-auto" strokeWidth={1.5} />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowDetailsModal(false)}>Sluiten</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HR Antwoorden Review Modal */}
      <Dialog open={viewHRAnswers} onOpenChange={setViewHRAnswers}>
        <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="pb-3 border-b border-neutral-100">
            <DialogTitle className="text-base font-semibold">Human Risk Assessment: antwoorden</DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">
              {hrAnswersData.length} vragen beantwoord {stats?.hr_assessment_score != null && `• Score: ${stats.hr_assessment_score}%`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1" data-testid="hr-view-answers-popup">
            {hrAnswersData.length > 0 ? hrAnswersData.map((a, i) => (
              <div key={i} className="p-4 bg-neutral-50 rounded-xl border border-neutral-100">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="text-xs font-mono text-neutral-400 mb-1">Vraag {i + 1}</p>
                    <p className="text-sm font-medium text-black">{a.question || a.question_text || `Vraag ${i + 1}`}</p>
                  </div>
                  <Badge className={`shrink-0 ${a.answer === 'ja' || a.answer === 'yes' ? 'bg-green-100 text-green-700' : a.answer === 'nee' || a.answer === 'no' ? 'bg-red-100 text-red-700' : a.answer === 'deels' || a.answer === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-neutral-100 text-neutral-700'}`}>
                    {a.answer === 'yes' ? 'Ja' : a.answer === 'no' ? 'Nee' : a.answer === 'partial' ? 'Deels' : a.answer || '-'}
                  </Badge>
                </div>
                {a.notes && (
                  <div className="mt-2 pt-2 border-t border-neutral-200">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Notitie</p>
                    <p className="text-xs text-neutral-600 italic">{a.notes}</p>
                  </div>
                )}
              </div>
            )) : (
              <p className="text-sm text-neutral-400 text-center py-6">Geen antwoorden beschikbaar</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default PeoplePage;
